import { useState } from "react";

import {
  useBindInspectionStepSave,
} from "../../../utils/useBindInspectionStepSave";

import { useLocalization } from "@i18n";

import { useSaveInspectionMutation } from "../../../api/inspection";
import { INSPECTION_GENERAL_COMMENTS_ITEM_KEY } from "../inspection-form.catalog";
import type { InspectionChecklistStepWrapperProps } from "./checklistStep.props";
import InspectionPhotoEvidence from "./InspectionPhotoEvidence";

/**
 * Step `generalComments` (form §7.3.10) — texto livre opcional + fotos
 * opcionais. Nunca conta anomalia e nunca bloqueia Salvar/Avançar.
 */
const InspectionGeneralCommentsStep = ({
  structureId,
  inspectionId,
  itemResponses,
  canWrite,
  attachments = [],
  inspectionStatus = "inProgress",
  onSaved,
  onBindSave,
}: InspectionChecklistStepWrapperProps) => {
  const { translate } = useLocalization();
  const persisted = itemResponses.find(
    (response) => response.itemKey === INSPECTION_GENERAL_COMMENTS_ITEM_KEY,
  );
  const [comments, setComments] = useState(
    typeof (persisted?.answerValue as { text?: unknown } | undefined)?.text ===
      "string"
      ? ((persisted?.answerValue as { text: string }).text ?? "")
      : (persisted?.observations ?? ""),
  );

  const { mutate: saveInspection } = useSaveInspectionMutation(
    structureId,
    inspectionId,
  );

  const inputId = "inspection-general-comments";

  const handleSave = () =>
    saveInspection(
      {
        itemResponses: [
          {
            itemKey: INSPECTION_GENERAL_COMMENTS_ITEM_KEY,
            occurrenceKey: "",
            answerValue: { text: comments },
          },
        ],
      },
      { onSuccess: (detail) => onSaved?.(detail) },
    );

  useBindInspectionStepSave(onBindSave, handleSave, canWrite);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-sm font-medium text-[var(--insp-text)]">
          {translate("inspection.form.generalComments.label")}
        </label>
        <textarea
          id={inputId}
          value={comments}
          rows={5}
          disabled={!canWrite}
          placeholder={translate("inspection.form.generalComments.placeholder")}
          onChange={(event) => setComments(event.target.value)}
          className="insp-inp"
        />
      </div>

      <InspectionPhotoEvidence
        structureId={structureId}
        inspectionId={inspectionId}
        itemKey={INSPECTION_GENERAL_COMMENTS_ITEM_KEY}
        canWrite={canWrite}
        inspectionStatus={inspectionStatus}
        admitsEvidence
        attachments={attachments.filter(
          (attachment) =>
            attachment.itemKey === INSPECTION_GENERAL_COMMENTS_ITEM_KEY,
        )}
      />

      {canWrite && !onBindSave ? (
        <button
          type="button"
          className="insp-btn insp-btn-primary self-end"
          onClick={handleSave}
        >
          {translate("inspection.form.saveAndContinue")}
        </button>
      ) : null}
    </div>
  );
};

export default InspectionGeneralCommentsStep;
